import React, { memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ProjectSidebarProps } from './types';
import { useProjectSidebar } from './useProjectSidebar';
import { SidebarWorkspaceSwitcher } from './SidebarWorkspaceSwitcher';
import { SidebarNavItems } from './SidebarNavItems';
import { SidebarProjectList } from './SidebarProjectList';
import { SidebarFooter } from './SidebarFooter';

export const ProjectSidebar: React.FC<ProjectSidebarProps> = () => {
  const {
    isCollapsed,
    setIsCollapsed,
    activeWorkspace,
    onWorkspaceChange,
    localViewMode,
    uiMode,
    isStrategyOpen,
    isStrategyActive,
    setStrategyManuallyToggled,
    handleSelect,
    onPrefetch,
    visibleProjects,
    localActiveId,
    openMenuId,
    setOpenMenuId,
    categories,
    user,
    onReorderProjects,
    onDuplicateProject,
    onRenameProject,
    handleArchiveToggle,
    onDeleteProject,
    onUpdateProjectCategory,
    isCreatingProject,
    onAddProject
  } = useProjectSidebar();

  return (
    <div 
      data-tour="project-sidebar"
      className={`bg-slate-100/90 dark:bg-slate-950/95 flex flex-col transition-all duration-300 relative z-[30] h-full group/sidebar backdrop-blur-md ${isCollapsed ? 'w-20' : 'w-80'}`}
      style={{ isolation: 'isolate' }}
    >
      {/* Toggle Button - Centered and only visible on hover */}
      <button 
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-full p-2 shadow-[0_10px_40px_rgba(0,0,0,0.3)] hover:bg-slate-50 dark:hover:bg-slate-700 transition-all z-[999] text-slate-600 dark:text-slate-400 hover:scale-110 active:scale-90 opacity-0 group-hover/sidebar:opacity-100 ring-4 ring-transparent hover:ring-indigo-500/10"
      >
        {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>

      {/* Workspace Switcher */}
      <SidebarWorkspaceSwitcher
        activeWorkspace={activeWorkspace}
        isCollapsed={isCollapsed}
        onWorkspaceChange={onWorkspaceChange}
      />

      {/* FIXED Global Navigation Items */}
      <SidebarNavItems
        localViewMode={localViewMode}
        isCollapsed={isCollapsed}
        uiMode={uiMode}
        isStrategyOpen={isStrategyOpen}
        isStrategyActive={isStrategyActive}
        onToggleStrategy={() => setStrategyManuallyToggled(!isStrategyOpen)}
        onSelect={handleSelect}
        onPrefetch={onPrefetch}
      />

      {/* SCROLLABLE Project List */}
      <SidebarProjectList
        visibleProjects={visibleProjects}
        activeWorkspace={activeWorkspace}
        isCollapsed={isCollapsed}
        localActiveId={localActiveId}
        localViewMode={localViewMode}
        openMenuId={openMenuId}
        categories={categories}
        currentUserId={user?.uid}
        currentUserEmail={user?.email}
        onReorderProjects={onReorderProjects}
        onSelect={handleSelect}
        onPrefetch={onPrefetch}
        onMenuToggle={setOpenMenuId}
        onDuplicateProject={onDuplicateProject}
        onRenameProject={onRenameProject}
        onArchiveToggle={handleArchiveToggle}
        onDeleteProject={onDeleteProject}
        onUpdateProjectCategory={onUpdateProjectCategory}
      />

      {/* Footer / Add Button */}
      <SidebarFooter
        isCollapsed={isCollapsed}
        isCreatingProject={isCreatingProject}
        onAddProject={onAddProject}
      />
    </div>
  );
};

export default memo(ProjectSidebar);
